import React, { useState } from 'react';
import { Alert, Form, Button, Card, Badge, FilterGrid, FilterField } from '@/components/ui';
import reportService from '../../../services/reportService';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import YellowHeaderInfo from '../components/YellowHeaderInfo';
import { ServiceArea } from '../../../services/areasService';

const statusOptions = [
  { value: 'FINALIZED', label: 'Finalized' },
  { value: 'CLOSED', label: 'Closed' }
];

interface D2PreviewData {
  id: number;
  recordType: string;
  serviceArea: string;
  recordId: string;
  taskNumber: string;
  activityNumber: string;
  startDate: string;
  endDate: string;
  accomplishment: string;
  unitOfMeasure: string;
  postedDate: string;
  highwayUnique: string;
  comments: string;
  status: string;
}

interface D2ReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateD2: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const D2ReportView: React.FC<D2ReportViewProps> = ({ serviceAreas, canCreateD2, loadingServiceAreas, defaultServiceAreaId }) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    statuses: ['CLOSED'] as string[],
    startDate: '',
    endDate: ''
  });
  const [previewData, setPreviewData] = useState<D2PreviewData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingPreliminary, setLoadingPreliminary] = useState(false);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false); // New state for filters collapse

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getD2PreviewData({
        serviceArea: filters.serviceArea,
        statuses: filters.statuses,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: pageSize || pagination.limit,
        sortField,
        sortDirection
      });
      setPreviewData(response.data);
      setPagination(response.pagination);
      showSuccessToast(`Generated preview with ${response.data.length} tasks (showing ${response.pagination.total} total)`);
    } catch (error) {
      setErrorMessage('Failed to generate D2 preview. Please try again later.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    handleGeneratePreview(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, limit: pageSize }));
    handleGeneratePreview(1, pageSize);
  };

  const handlePreliminaryExport = async (customFilename?: string) => {
    setLoadingPreliminary(true);
    setErrorMessage(null);
    try {
      if (!filters.serviceArea) {
        setErrorMessage('Please select a service area');
        setLoadingPreliminary(false);
        return;
      }
      const now = new Date();
      await reportService.downloadD2Report({
        serviceArea: filters.serviceArea,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        preliminary: true,
        statuses: filters.statuses,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });
      showSuccessToast('Preliminary D2 report exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      setErrorMessage('Failed to export D2 report. Please try again later.');
    } finally {
      setLoadingPreliminary(false);
    }
  };

  const handleFinalExport = async (postedDate: string) => {
    setLoadingFinal(true);
    setErrorMessage(null);
    try {
      if (!filters.serviceArea) {
        setErrorMessage('Please select a service area');
        setLoadingFinal(false);
        return;
      }
      const now = new Date();
      await reportService.downloadD2Report({
        serviceArea: filters.serviceArea,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        preliminary: false,
        postedDate,
        statuses: filters.statuses,
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      showSuccessToast('Final D2 report exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      setErrorMessage('Failed to export D2 report. Please try again later.');
    } finally {
      setLoadingFinal(false);
    }
  };

  // Generate default filename based on format: [PRELIM_]SAXX_D2_YYYYMM
  const generateDefaultFilename = (isPreliminary: boolean = false) => {
    if (filters.serviceArea) {
      const serviceAreaCode = filters.serviceArea.replace(/\D/g, '') || '00';
      const now = new Date();
      const yearMonth = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      const prefix = isPreliminary ? 'PRELIM_' : '';
      return `${prefix}SA${serviceAreaCode.padStart(2, '0')}_D2_${yearMonth}`;
    }
    return isPreliminary ? 'PRELIM_D2_report' : 'D2_report';
  };

  const handleFinalExportWithFilename = async (postedDate: string, customFilename?: string) => {
    setLoadingFinal(true);
    setErrorMessage(null);
    try {
      if (!filters.serviceArea) {
        setErrorMessage('Please select a service area');
        setLoadingFinal(false);
        return;
      }
      const now = new Date();
      await reportService.downloadD2Report({
        serviceArea: filters.serviceArea,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        preliminary: false,
        postedDate,
        statuses: filters.statuses,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename
      });
      showSuccessToast('Final D2 report exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      setErrorMessage('Failed to export D2 report. Please try again later.');
    } finally {
      setLoadingFinal(false);
    }
  };

  const columns = [
    { 
      label: 'Record Type', 
      key: 'recordType',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Service Area', 
      key: 'serviceArea',
      sortable: true,
      width: '140px'
    },
    { 
      label: 'Record Number', 
      key: 'recordId',
      sortable: false, // DISABLED - prevents memory leak (requires in-memory sort)
      width: '150px'
    },
    { 
      label: 'Task Number', 
      key: 'taskNumber',
      sortable: true,
      width: '140px'
    },
    { 
      label: 'Activity Number', 
      key: 'activityNumber',
      sortable: true,
      width: '150px'
    },
    { 
      label: 'Start Date', 
      key: 'startDate',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'End Date', 
      key: 'endDate',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Accomplishment', 
      key: 'accomplishment',
      sortable: true,
      width: '150px'
    },
    { 
      label: 'Unit of Measure', 
      key: 'unitOfMeasure',
      sortable: true,
      width: '150px'
    },
    { 
      label: 'Posted Date', 
      key: 'postedDate',
      sortable: true,
      width: '120px',
      render: (item: D2PreviewData) => (
        <span className="tw-text-content-muted">
          {item.postedDate}
        </span>
      ),
      headerTooltip: "Posted Date will be set at the time of finalized report export",
      headerContent: (
        <div className="tw-flex tw-items-center">
          <span>Posted Date</span>
          <i className="bi bi-info-circle tw-text-info tw-ml-1" style={{ fontSize: '0.75rem' }} title="Posted Date will be set at the time of finalized report export"></i>
        </div>
      )
    },
    { 
      label: 'Highway Unique', 
      key: 'highwayUnique',
      sortable: false, // DISABLED - prevents memory leak (requires in-memory sort)
      width: '170px'
    },
    { 
      label: 'Comments', 
      key: 'comments',
      sortable: true,
      width: '250px'
    },
    {
      label: 'Status',
      key: 'status',
      sortable: true,
      width: '120px',
      render: (item: D2PreviewData) => (
        <Badge bg="warning">
          {item.status.replace('_', ' ')}
        </Badge>
      )
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview(1)}
        onExport={() => {}} // Remove export functionality from top
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true} // Disable export button in top bar
        generateButtonText="Run Report"
        hideExportButton={true} // Hide the export button entirely
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed} // Track collapse state
      >
        <FilterGrid>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
            >
              <option value="">All Service Areas</option>
              {serviceAreas.map((area: ServiceArea) => (
                <option key={area?.id || Math.random()} value={area?.id?.toString() || ''}>
                  {area?.name || area?.code || 'Unknown'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Task Status</Form.Label>
            <MultiSelectDropdown
              options={statusOptions}
              value={filters.statuses}
              onChange={(selected) => setFilters(prev => ({ ...prev, statuses: selected.map(s => String(s)) }))}
              formatOption={(option) => option.label}
              getValue={(option) => option.value}
              placeholder="Select statuses"
            />
          </Form.Group>
          <FilterField span={2}>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Completion Date Range</Form.Label>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  style={{ width: '140px' }}
                />
                <span className="tw-text-content-muted tw-text-sm">to</span>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  style={{ width: '140px' }}
                />
              </div>
            </Form.Group>
          </FilterField>
        </FilterGrid>
      </ReportParametersCard>
      </div>
      
      {/* Yellow Header Information */}
      <YellowHeaderInfo />
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} tasks)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!filters.serviceArea}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }}
            loading={loadingPreview}
            emptyMessage="No data available. Click 'Run Report' to load data."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed} // Pass collapse state to table
            yellowHeaderColumn="status" // Make Status column header yellow
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handlePreliminaryExport}
        onFinalExport={handleFinalExportWithFilename}
        loadingPreliminary={loadingPreliminary}
        loadingFinal={loadingFinal}
        showFilenameInput={true}
        generateFilename={generateDefaultFilename}
        size="small"
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="success"
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default D2ReportView;